/**
 * @file   src/helpers/matchDemandUtils.js
 * @brief  Tier detection, insight text generation, and demand analysis for Match tab
 */
import {
  formatCompactPrice,
  computeDemandBins,
  computeSweetSpot,
  findBinIndexForPrice,
  sweetSpotLabelRange,
} from "./priceCapUtils";
import { BUY, LONG_RENT } from "../constants/common";

/**
 * Detect which tier of the demand display to show based on match count.
 *
 * Tier 1: 5+ matches - full experience (histogram, slider, stats, insight)
 * Tier 2: 3-4 matches - simplified (stats strip + insight, no histogram)
 * Tier 3: 1-2 matches - minimal (match count + encouragement)
 * Tier 4: 0 matches - notification promise
 *
 * @param {number} matchCount - Total number of matches
 * @returns {1|2|3|4}
 */
export const detectTier = (matchCount) => {
  if (matchCount >= 5) return 1;
  if (matchCount >= 3) return 2;
  if (matchCount >= 1) return 3;
  return 4;
};

/**
 * Get the price label for the ad type (purchase price vs rent vs nightly).
 */
const getPriceLabel = (adType) => {
  if (adType === BUY) return "price range";
  if (adType === LONG_RENT) return "rent range";
  return "nightly rate range";
};

/**
 * Build contextual insight text based on asking price, slider position, and demand data.
 *
 * @param {number} askingPrice - The property's asking price
 * @param {number} sliderPrice - Current slider position (may differ from asking)
 * @param {number[]} prices - Array of match budget prices
 * @param {number} matchCount - Total match count
 * @param {string} adType - BUY, LONG_RENT, or SHORT_RENT
 * @returns {string} Human-readable insight text
 */
export const buildInsightText = (
  askingPrice,
  sliderPrice,
  prices,
  matchCount,
  adType,
  { rangeMin, rangeMax } = {},
) => {
  // Tier 4 - no matches
  if (!matchCount || matchCount === 0) {
    return "You'll be notified when more agents post Want-Listings that match your property.";
  }

  // Tier 3 - very few matches
  if (matchCount <= 2) {
    return `${matchCount} active agent${matchCount > 1 ? "s" : ""} in your ${getPriceLabel(adType)}. More agents are posting Want-Listings daily.`;
  }

  // Tier 2 - some matches but not enough for histogram
  if (matchCount <= 4) {
    return `${matchCount} active buyers in your ${getPriceLabel(adType)}.`;
  }

  // Tier 1 - full experience with slider interaction
  if (!prices || prices.length === 0 || !askingPrice) {
    return `${matchCount} active buyers looking in this area.`;
  }

  const demand = computeDemandBins(prices, rangeMin, rangeMax);
  if (!demand) {
    return `${matchCount} active buyers in your ${getPriceLabel(adType)}.`;
  }

  if (!sliderPrice || sliderPrice === askingPrice) {
    const sweetSpot = computeSweetSpot(demand);
    // "Most" is only true when the band holds over half of all buyers.
    if (sweetSpot && sweetSpot.count > prices.length / 2) {
      const label = sweetSpotLabelRange(sweetSpot);
      return `Most buyer activity around ${formatCompactPrice(label.min)} - ${formatCompactPrice(label.max)}.`;
    }
    const half = prices.length / 2;
    if (demand.above > half) {
      return `Most buyer budgets sit above ${formatCompactPrice(rangeMax)}.`;
    }
    if (demand.below > half) {
      return `Most buyer budgets sit below ${formatCompactPrice(rangeMin)}.`;
    }
    return `${matchCount} active buyers in your ${getPriceLabel(adType)}.`;
  }

  const diff = sliderPrice - askingPrice;
  const pctDiff = Math.abs(diff / askingPrice * 100).toFixed(0);
  const direction = diff > 0 ? "above" : "below";
  const binIdx = findBinIndexForPrice(demand.bins, sliderPrice);
  const nearCount = binIdx == null ? 0 : demand.bins[binIdx].count;

  if (nearCount > 0) {
    return `${nearCount} buyer${nearCount > 1 ? "s" : ""} active near ${formatCompactPrice(sliderPrice)} (${pctDiff}% ${direction} your price).`;
  }
  return `No buyer activity at ${formatCompactPrice(sliderPrice)} (${pctDiff}% ${direction} your price).`;
};
